import React from "react";
import {
  Box,
  Collapse,
  FormHelperText,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  ContentCopyTwoTone,
  Delete,
  ExpandLess,
  ExpandMore,
  Info,
  Remove,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../../Store/actions";
import TextInputField from "../../../../components/commonComponents/TextInputs/TextInputField";
import TimePickerField from "../../../../components/commonComponents/TextInputs/TimePickerField";
import DateInputField from "../../../../components/commonComponents/TextInputs/DateInputs";
import moment from "moment-timezone";
import {
  HtmlTooltip,
  HtmlTooltipDisabled,
  StyledCheckbox,
  StyledGreyInfoBox,
  StyledSwitch,
} from "../../../../components/commonComponents/StyledComponents/styled";
import ActionButton from "../../../../components/commonComponents/Buttons/ActionButton";
import Confirmation from "../../../../components/commonComponents/Confirmation";
import { nextLocalScheduleId, WEEKDAY_KEYS, WEEKDAY_DISPLAY_ORDER, formatDateDMY } from "../../../../api_calls/utils";
import { isInvertedRange, normalizeCloseTime } from "../../../../api_calls/infrastructure";

// Calendar highlight colors for the validity date pickers. The red matches the
// sidebar's logout area family (rgba(240, 75, 85, 0.09)) with more alpha — on a
// 28px day cell the 0.09 tint is barely visible.
const SCHEDULE_HIGHLIGHTS = [
  {
    key: "active",
    color: "rgba(240, 75, 85, 0.25)",
    borderColor: "rgba(240, 75, 85, 0.5)",
    label: "Active schedules",
  },
  {
    key: "inactive",
    color: "rgba(98, 98, 110, 0.2)",
    borderColor: "rgba(98, 98, 110, 0.5)",
    label: "Deactivated schedules",
  },
];

// How long a schedule stays exempt from the Show Expired filter after losing
// focus. Covers the field's own blur validation (which clears an End date below
// today), the picker's delayed blur retries and the schedule name's debounce.
const EXPIRY_GRACE_MS = 2000;

// How long to let an errored card's Collapse finish opening before scrolling to
// the error inside it. MUI's timeout="auto" reaches roughly 400ms at full card
// height; scrolling earlier would aim at a position that is still moving.
const COLLAPSE_SETTLE_MS = 450;

const EXPIRED_LOCK_TOOLTIP = "Not available after the schedule has expired";
const ACTIVE_HOURS_LOCK_TOOLTIP = "Not available while the schedule is active";

// Date-based expiry only — ignores the saved active flag.
const isExpiredValidity = (s, today) =>
  !s.no_end && !!s.validity_end && s.validity_end < today;

const WEEKDAY_LABELS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

// The default data of a fresh schedule, without id/is_collapsed. Split out so a
// pristine-check can compare a card against it without minting a local id (which
// nextLocalScheduleId would, as a side effect).
const DEFAULT_SCHEDULE_DATA = () => ({
  name: "",
  validity_start: null,
  validity_end: null,
  no_end: false,
  active: true,
  same_times_all_days: false,
  same_times_is_24h: false,
  same_times_ranges: [{ open: "08:00", close: "20:00" }],
  weekdays: WEEKDAY_KEYS.reduce((acc, day) => {
    acc[day] = { open: true, is_24h: false, time_ranges: [{ open: "08:00", close: "20:00" }] };
    return acc;
  }, {}),
});

// A freshly created card is pristine when its data still equals DEFAULT_SCHEDULE_DATA
// (id and is_collapsed are UI-only and ignored). Derived from the same factory, so
// key order matches and a JSON compare is reliable.
const isPristineSchedule = (schedule) => {
  const { id, is_collapsed, ...data } = schedule;
  return JSON.stringify(data) === JSON.stringify(DEFAULT_SCHEDULE_DATA());
};

const DEFAULT_SCHEDULE = () => ({
  id: nextLocalScheduleId(),
  is_collapsed: false,
  ...DEFAULT_SCHEDULE_DATA(),
});

const detectOverlap = (ranges, idx) => {
  const r = ranges[idx];
  if (!r || !r.open || !r.close) return false;
  // Treat end-of-day picks (00:00, 23:55) as 23:59 so a 23:40 → 00:00 range is
  // correctly compared against earlier same-day ranges.
  const rClose = normalizeCloseTime(r.close);
  return ranges.some((other, i) => {
    if (i === idx) return false;
    if (!other.open || !other.close) return false;
    const otherClose = normalizeCloseTime(other.close);
    return !(rClose <= other.open || r.open >= otherClose);
  });
};

// Computes live error flags for the Schedule Overview box.
const computeScheduleErrors = (schedules) => {
  const errors = {};
  const today = new Date().toISOString().slice(0, 10);
  const activeSchedules = schedules.filter(
    (s) => s.active !== false && !isExpiredValidity(s, today)
  );

  const noEndCount = activeSchedules.filter((s) => s.no_end).length;
  if (noEndCount !== 1) {
    errors._noEnd =
      noEndCount === 0
        ? "Exactly one schedule must be defined without an end date"
        : "Only one schedule can have no end date";
  }

  activeSchedules.forEach((schedule) => {
    const hasOpenDay = WEEKDAY_KEYS.some((d) => schedule.weekdays?.[d]?.open === true);
    if (!hasOpenDay) {
      errors["days_" + schedule.id] = "At least one day must be open";
    }
  });

  const sorted = [...activeSchedules]
    .filter((s) => s.validity_start)
    .sort((a, b) => (a.validity_start > b.validity_start ? 1 : -1));


  if (sorted.length > 0 && sorted[0].validity_start > today) {
    errors["no_coverage_" + sorted[0].id] =
      "The store has no opening hours before this schedule.";
  }

  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (a.no_end || (a.validity_end && a.validity_end >= b.validity_start)) {
      errors["overlap_" + a.id] = "This schedule overlaps with another schedule";
      errors["overlap_" + b.id] = "This schedule overlaps with another schedule";
    }
  }

  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (a.no_end || !a.validity_end) continue;
    const endPlusOne = new Date(a.validity_end);
    endPlusOne.setDate(endPlusOne.getDate() + 1);
    const endPlusOneStr = endPlusOne.toISOString().slice(0, 10);
    if (endPlusOneStr !== b.validity_start) {
      errors["gap_after_" + a.id] = "There is a gap after this schedule";
      errors["gap_before_" + b.id] = "There is a gap before this schedule";
    }
  }

  return errors;
};

// Error messages can be long, so cap the width and wrap onto multiple lines
// instead of growing the column and pushing surrounding controls sideways.
const wrappedHelperSx = {
  ml: 0,
  maxWidth: 250,
  whiteSpace: "normal",
  overflowWrap: "break-word",
  lineHeight: 1.3,
};

const TimeRangeRow = ({ range, idx, ranges, showLabels, isFirst, emptyError, disabled, lockTooltip = EXPIRED_LOCK_TOOLTIP, onChangeOpen, onChangeClose, onAdd, onRemove }) => {
  const hasOverlap = detectOverlap(ranges, idx);
  const inverted = isInvertedRange(range.open, range.close);
  // Inverted/overlap derive live and clear on the next render. The empty error
  // is save-triggered (emptyError is set only by the save validation) but still
  // clears live once both times are filled, so it behaves like the others and
  // spans the whole row rather than sitting under a single field.
  const isEmpty = !range.open || !range.close;
  const errorMsg = inverted
    ? "End time must be after start time"
    : hasOverlap
      ? "Overlaps with another time range"
      : emptyError && isEmpty
        ? "Opening and closing time required"
        : "";

  // Extra top headroom only when several slots are stacked — a single slot stays
  // tight; the labeled first row otherwise sits flush under the section divider.
  const multiSlotTopPad = isFirst && ranges.length > 1 ? 0.5 : 0;

  return (
    <Box sx={{ pt: multiSlotTopPad }}>
      <Stack direction="row" spacing={1} alignItems="flex-end">
        <Box sx={{ width: 90 }}>
          <HtmlTooltipDisabled title={disabled ? lockTooltip : ""}>
            <span>
              <TimePickerField
                elemId={"open_" + idx}
                label={showLabels ? "Open" : ""}
                defaultVal={range.open}
                disabled={disabled}
                onChangeCall={onChangeOpen}
              />
            </span>
          </HtmlTooltipDisabled>
        </Box>

        {/* Matches the field's own padding below the value, so "to" sits on the
            value's line — with or without labels. */}
        <Typography variant="body2" sx={{ pb: 1, ...(disabled && { color: "text.disabled" }) }}>
          to
        </Typography>

        <Box sx={{ width: 90 }}>
          <HtmlTooltipDisabled title={disabled ? lockTooltip : ""}>
            <span>
              <TimePickerField
                elemId={"close_" + idx}
                label={showLabels ? "Close" : ""}
                defaultVal={range.close}
                disabled={disabled}
                onChangeCall={onChangeClose}
              />
            </span>
          </HtmlTooltipDisabled>
        </Box>

        {/* + on the labeled first row: pin to the top, then offset into the
            value band (tuned vs − on unlabeled rows). */}
        {isFirst && ranges.length < 5 && (
          <Tooltip title={disabled ? lockTooltip : "Add time range"}>
            <Box
              component="span"
              sx={{ display: "inline-flex", alignSelf: "flex-start", pt: "14px" }}
            >
              <IconButton size="small" onClick={onAdd} disabled={disabled}>
                <Add fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
        {!isFirst && (
          <Tooltip title={disabled ? lockTooltip : "Remove time range"}>
            <Box component="span" sx={{ display: "inline-flex", pb: 0.5 }}>
              <IconButton size="small" onClick={onRemove} disabled={disabled}>
                <Remove fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Stack>

      {errorMsg && (
        <FormHelperText error sx={wrappedHelperSx}>{errorMsg}</FormHelperText>
      )}

      {/* Trailing gap between stacked ranges (and, for the last range, feeding the
          last weekday row's pinned bottom gap). */}
      <Box sx={{ pb: 1.5 }} />
    </Box>
  );
};
const DAY_ROW_PAD = 3;
const LAST_DAY_ROW_EXTRA = 4;
const DAY_ROW_MIN_HEIGHT = 60;

const WeekdayRow = ({ day, dayData, sameTimesActive, disabled, lockTooltip = EXPIRED_LOCK_TOOLTIP, onUpdate, isLast, scheduleId, fieldErrors, setErrorRef }) => {
  const handleAddRange = () => {
    if (dayData.time_ranges.length >= 5) return;
    onUpdate({ time_ranges: [...dayData.time_ranges, { open: "", close: "" }] });
  };

  const handleRemoveRange = (idx) => {
    onUpdate({ time_ranges: dayData.time_ranges.filter((_, i) => i !== idx) });
  };

  const handleTimeChange = (idx, field, value) => {
    const updated = dayData.time_ranges.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
    onUpdate({ time_ranges: updated });
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        pt: `${DAY_ROW_PAD}px`,
        pb: `${DAY_ROW_PAD + (isLast ? LAST_DAY_ROW_EXTRA : 0)}px`,
        // Negative horizontal margin + positive padding lets the closed-day
        // background highlight span the full width of the schedule card,
        // breaking out of the parent's p:2 inner padding.
        mx: -2,
        px: 2,
        borderBottom: isLast ? "none" : "1px solid #f0f0f0",
        bgcolor: !dayData.open ? "#E6E6E6" : "transparent",
        minHeight: DAY_ROW_MIN_HEIGHT + (isLast ? LAST_DAY_ROW_EXTRA : 0),
      }}
    >
      {/* Day toggle + name — fixed width, always visible */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: 150, flexShrink: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: "bold", width: 90, ...(disabled && { color: "text.disabled" }) }}
        >
          {WEEKDAY_LABELS[day]}
        </Typography>
        <HtmlTooltipDisabled title={disabled ? lockTooltip : ""}>
          <Box component="span" sx={{ display: "inline-flex" }}>
            <StyledSwitch
              checked={dayData.open}
              disabled={disabled}
              onChange={(e) => {
                const patch = { open: e.target.checked };
                if (e.target.checked && (!dayData.time_ranges || dayData.time_ranges.length === 0)) {
                  patch.time_ranges = [{ open: "08:00", close: "20:00" }];
                }
                onUpdate(patch);
              }}
              size="small"
            />
          </Box>
        </HtmlTooltipDisabled>
      </Stack>

      {/* Dynamic gap — fills available space between name and time controls */}
      <Box sx={{ flex: 1 }} />

      {/* When Same Times All Days is on, each weekday only shows "Open" or "Closed".
          Always vertically centered — pinning the last row to flex-end threw
          Sunday's Closed/Open out of line with every other weekday. */}
      {sameTimesActive && (
        <Box sx={{ minWidth: 250, alignSelf: "center" }}>
          <Typography
            variant="body2"
            sx={{ textAlign: "left", color: disabled ? "text.disabled" : "#62626E" }}
          >
            {dayData.open ? "Open" : "Closed"}
          </Typography>
        </Box>
      )}

      {/* Time controls — right-aligned, only visible when sameTimesActive is off.
          On the last row with real timeslots, alignSelf pins to the bottom edge
          so the gap under the last slot stays exactly pb (minHeight slack would
          otherwise split above and below). 24h is a single-line label like
          Closed — keep it centered or Sunday drifts just like the Closed bug. */}
      {!sameTimesActive && dayData.open && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ alignSelf: isLast && !dayData.is_24h ? "flex-end" : "center" }}
        >
          <Box>
            <HtmlTooltipDisabled title={disabled ? lockTooltip : ""}>
              <span>
                <ToggleButton
                  value="24h"
                  selected={dayData.is_24h}
                  disabled={disabled}
                  onChange={() => onUpdate({ is_24h: !dayData.is_24h })}
                  size="small"
                  sx={{
                    height: 32,
                    px: 1.5,
                    border: "1px solid #ccc",
                    "&.Mui-selected": {
                      bgcolor: "#AF3241",
                      color: "#fff",
                      "&:hover": { bgcolor: "#8a2833" },
                    },
                  }}
                >
                  <Typography variant="caption">24h</Typography>
                </ToggleButton>
              </span>
            </HtmlTooltipDisabled>
          </Box>

          <Box sx={{ minWidth: 250 }}>
            {dayData.is_24h ? (
              <Box sx={{ height: 32, display: "flex", alignItems: "center" }}>
                <Typography
                  variant="body2"
                  sx={{ textAlign: "left", color: disabled ? "text.disabled" : "#62626E" }}
                >
                  Open 24 Hours
                </Typography>
              </Box>
            ) : (
              <Stack spacing={0.75}>
                {dayData.time_ranges.map((range, idx) => (
                  <Box
                    key={idx}
                    ref={(el) => setErrorRef(`time_gap_${scheduleId}_${day}_${idx}`, el)}
                  >
                    <TimeRangeRow
                      range={range}
                      idx={idx}
                      ranges={dayData.time_ranges}
                      showLabels={idx === 0}
                      isFirst={idx === 0}
                      disabled={disabled}
                      emptyError={fieldErrors?.[`time_gap_${scheduleId}_${day}_${idx}`] || ""}
                      lockTooltip={lockTooltip}
                      onChangeOpen={(val) => handleTimeChange(idx, "open", val)}
                      onChangeClose={(val) => handleTimeChange(idx, "close", val)}
                      onAdd={handleAddRange}
                      onRemove={() => handleRemoveRange(idx)}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}

      {/* Closed label stays centered on every row, including Sunday (isLast).
          flex-end was only needed for multi-slot time columns below. */}
      {!sameTimesActive && !dayData.open && (
        <Box sx={{ minWidth: 250, alignSelf: "center" }}>
          <Typography
            variant="body2"
            sx={{ textAlign: "left", color: disabled ? "text.disabled" : "#62626E" }}
          >
            Closed
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

// The Schedule Name input is isolated into its own leaf so a keystroke only
// re-renders this small field — not the whole ScheduleCard (7 weekday rows plus
// every time/date picker), which is what made typing feel laggy. Local state
// keeps the input instant; the store (and the Schedule Overview) update on a
// debounce so the expensive re-render happens once the user pauses, not on every
// keystroke. Blur flushes any pending edit immediately so Next/Save always
// validates the latest name.
const ScheduleNameField = ({ scheduleId, initialName, fieldError, disabled, onCommit }) => {
  const [nameValue, setNameValue] = React.useState(initialName || "");
  const debounceRef = React.useRef(null);
  const latestRef = React.useRef(initialName || "");
  // Commit through the latest onCommit so a delayed edit merges into the current
  // schedules snapshot instead of overwriting concurrent edits to other schedules
  // with the stale closure captured when the timer was scheduled.
  const onCommitRef = React.useRef(onCommit);
  onCommitRef.current = onCommit;

  const handleChange = (e) => {
    const val = e.target.value;
    setNameValue(val);
    latestRef.current = val;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      onCommitRef.current(latestRef.current);
    }, 2000);
  };

  const flush = () => {
    if (!debounceRef.current) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = null;
    onCommitRef.current(latestRef.current);
  };

  React.useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  // The persisted "name required" error clears the moment the field has a value,
  // so a fixed field stops showing the error immediately instead of lingering
  // until the next Next/Save.
  const shownError = fieldError && nameValue.trim() ? "" : fieldError;

  return (
    // onBlur (focusout bubbles in React) flushes a pending debounced edit before
    // the field loses focus — e.g. when Next/Save is clicked.
    <Box sx={{ width: 500, flexShrink: 0 }} onBlur={flush}>
      <TextInputField
        elemId={"schedule_name_" + scheduleId}
        label="Schedule Name*"
        fieldName="schedule_name"
        showlabel={true}
        defaultVal={nameValue}
        inputType={"text"}
        readOnlyFlag={disabled}
        onChangeCall={handleChange}
        fieldError={shownError}
        // Drop the field's normal margins locally (without changing TextInputField):
        // top margin was the oversized gap above Schedule Name; bottom margin is
        // dropped so the box bottom lands on the input underline — the shared
        // baseline the right-side controls align to via the row's flex-end.
        sx={{ mt: 0, mb: 0 }}
      />
    </Box>
  );
};

const ScheduleCard = ({ schedule, schedules, usedFor, loadedValidity, onUpdate, onDelete, onCopy, fieldErrors, setErrorRef }) => {
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = moment(today, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");
  const fieldErr = (key) => (fieldErrors && fieldErrors[key]) ? fieldErrors[key] : "";
  const noEndTakenByOther = schedules.some((s) => s.no_end && s.id !== schedule.id && s.active !== false);

  // Locks use loaded validity, not live edits, so a card filled in this session
  // does not lock itself while the user is still typing dates.
  const loaded = loadedValidity || {};
  const startLocked =
    usedFor === "update" && !!loaded.validity_start && loaded.validity_start <= today;
  const editLocked = startLocked && isExpiredValidity(loaded, today);
  const hoursLocked = startLocked;
  const hoursLockTooltip = editLocked ? EXPIRED_LOCK_TOOLTIP : ACTIVE_HOURS_LOCK_TOOLTIP;
  // Edit normally forbids start=today. If nothing currently covers today, allow
  // it so a store with no active schedule can still close the coverage gap.
  const hasActiveSchedule = schedules.some(
    (s) =>
      s.id !== schedule.id &&
      s.active !== false &&
      s.validity_start &&
      s.validity_start <= today &&
      !isExpiredValidity(s, today)
  );
  const startMinDate = usedFor === "update" && hasActiveSchedule ? tomorrow : today;
  const onlyOneLeft = schedules.length === 1;
  const deleteDisabled = onlyOneLeft || startLocked;
  const deleteTooltip = startLocked
    ? (editLocked ? EXPIRED_LOCK_TOOLTIP : ACTIVE_HOURS_LOCK_TOOLTIP)
    : onlyOneLeft
      ? "You cannot delete the last schedule"
      : "Delete";
  const noEndBlocked = editLocked || noEndTakenByOther;
  const noEndBlockedReason = editLocked
    ? EXPIRED_LOCK_TOOLTIP
    : noEndTakenByOther
      ? "Only one schedule can have no end date"
      : "";

  const sameTimesRanges = schedule.same_times_ranges || [{ open: "08:00", close: "20:00" }];

  // Save-time field errors live in the parent's state and otherwise persist until
  // the next Next/Save. Clear an "empty field required" error the moment its field
  // is filled, so a fixed field stops showing the error immediately instead of
  // lingering. (The name field handles its own error suppression internally;
  // overlap/gap/coverage errors in the Schedule Overview already recompute live.)
  const showFieldErr = (key) => {
    const msg = fieldErr(key);
    if (!msg) return "";
    if (key === "start_" + schedule.id) return schedule.validity_start ? "" : msg;
    if (key === "end_" + schedule.id) {
      if (schedule.no_end) return "";
      if (msg === "End date required") return schedule.validity_end ? "" : msg;
    }
    if (key === "days_" + schedule.id) {
      return WEEKDAY_KEYS.some((d) => schedule.weekdays?.[d]?.open) ? "" : msg;
    }
    return msg;
  };

  // Marks calendar days already covered by another schedule so the user sees
  // where a new validity range would collide. Compares local date parts as
  // YYYY-MM-DD strings (same convention as validity_start/end); toISOString
  // would shift the day near midnight in timezones ahead of UTC.
  const otherSchedules = schedules.filter((s) => s.id !== schedule.id && s.validity_start);
  const coversDate = (s, d) => {
    if (d < s.validity_start) return false;
    if (s.no_end) return true;
    // A schedule with a start but no end yet only occupies its start day.
    return s.validity_end ? d <= s.validity_end : d === s.validity_start;
  };
  // Active schedules take precedence over deactivated ones on the same day.
  const highlightForDate = (dateObj) => {
    const d = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")}`;
    if (otherSchedules.some((s) => s.active !== false && coversDate(s, d))) return "active";
    if (otherSchedules.some((s) => s.active === false && coversDate(s, d))) return "inactive";
    return null;
  };
  // Only legend entries that can actually appear in this picker. An empty array
  // still swaps in the legend action bar (dropping the Clear button) but renders
  // nothing.
  const scheduleLegend = SCHEDULE_HIGHLIGHTS.filter((item) =>
    otherSchedules.some((s) => (s.active !== false) === (item.key === "active"))
  );

  const validityText = () => {
    const start = schedule.validity_start ? formatDateDMY(schedule.validity_start) : "—";
    const end = schedule.no_end ? "Indefinitely" : schedule.validity_end ? formatDateDMY(schedule.validity_end) : "—";
    return `Valid ${start} – ${end}`;
  };

  const updateWeekday = (day, patch) => {
    onUpdate({ weekdays: { ...schedule.weekdays, [day]: { ...schedule.weekdays[day], ...patch } } });
  };

  const handleUpdateSameTimesRanges = (updatedRanges) => {
    onUpdate({ same_times_ranges: updatedRanges });
  };

  const handleAddSameTimesRange = () => {
    if (sameTimesRanges.length >= 5) return;
    handleUpdateSameTimesRanges([...sameTimesRanges, { open: "", close: "" }]);
  };

  const handleRemoveSameTimesRange = (idx) => {
    handleUpdateSameTimesRanges(sameTimesRanges.filter((_, i) => i !== idx));
  };

  const handleSameTimesChange = (idx, field, value) => {
    handleUpdateSameTimesRanges(sameTimesRanges.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  return (
    <Paper variant="outlined" sx={{ mb: 2, borderRadius: 1 }}>
      <Stack
        direction="row"
        alignItems="flex-end"
        // pt matches the in-section header→field gap (Validity / Opening Schedule)
        // so the Schedule Name top spacing is no longer the odd long outlier.
        sx={{ p: 1.5, pt: 1.5, borderBottom: schedule.is_collapsed ? "none" : "1px solid #e0e0e0" }}
      >
        {/* flexShrink stays on the flex item, which is now this wrapper */}
        <Box ref={(el) => setErrorRef("name_" + schedule.id, el)} sx={{ flexShrink: 0 }}>
          <ScheduleNameField
            scheduleId={schedule.id}
            initialName={schedule.name}
            fieldError={fieldErr("name_" + schedule.id)}
            disabled={false}
            onCommit={(name) => onUpdate({ name })}
          />
        </Box>

        {/* Spacer pushes validity + controls to the right edge */}
        <Box sx={{ flex: 1 }} />

        {/* Right cluster  */}
        <Stack direction="row" alignItems="center">
          <Typography variant="caption" sx={{ color: "#62626E" }}>
            {validityText()}
          </Typography>

          {/* Activate/deactivate schedules is temporarily disabled.
          {usedFor === "update" && (
            <HtmlTooltipDisabled
              title={
                editLocked
                  ? EXPIRED_LOCK_TOOLTIP
                  : onlyOneActiveLeft
                    ? "At least one schedule must be active"
                    : schedule.active !== false
                      ? "Disable Schedule"
                      : "Enable Schedule"
              }
            >
              <Box component="span" sx={{ display: "inline-flex", ml: 2, mb: 0.1 }}>
                <StyledSwitch
                  checked={schedule.active !== false}
                  disabled={editLocked || onlyOneActiveLeft}
                  size="small"
                  onChange={(e) => onUpdate({ active: e.target.checked })}
                />
              </Box>
            </HtmlTooltipDisabled>
          )}
          */}

          <HtmlTooltip title="Copy Schedule">
            <IconButton size="small" onClick={onCopy} sx={{mr: -0.5, ml: 2.3, mb: 0.2, color: "#AF3241" }}>
              <ContentCopyTwoTone fontSize="small" />
            </IconButton>
          </HtmlTooltip>

          {deleteDisabled ? (
            <HtmlTooltipDisabled title={deleteTooltip}>
              <Box component="span" sx={{ display: "inline-flex", ml: 1.5, mb: 0.2 }}>
                <IconButton size="small" disabled>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </HtmlTooltipDisabled>
          ) : (
            <HtmlTooltip title="Delete">
              <IconButton size="small" onClick={onDelete} sx={{ ml: 1.5, mb: 0.2, color: "#AF3241" }}>
                <Delete fontSize="small" />
              </IconButton>
            </HtmlTooltip>
          )}
        </Stack>

        {/* Expand bottom-aligns with the rest of the right cluster on the input
            underline; margins only nudge horizontal position. */}
        <IconButton
          size="small"
          sx={{ ml: 0.5, mr: "-4px" }}
          onClick={() => onUpdate({ is_collapsed: !schedule.is_collapsed })}
        >
          {schedule.is_collapsed ? <ExpandMore /> : <ExpandLess />}
        </IconButton>
      </Stack>

      <Collapse in={!schedule.is_collapsed}>
        {/* Section padding uses the same 1.5 unit as header→field gaps so the
            red between-section spacings match the green in-section ones. */}
        <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>

          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: "bold" }}>
            Validity:
          </Typography>
          {/* Top-aligned, not bottom-aligned: reserveErrorSpace only keeps one
              error line free, so a two-line error made a bottom-aligned row push
              that field's label and input upward and break the alignment with its
              neighbour. Aligned at the top, every date field puts its label and
              input at the same height and the error grows downward instead. */}
          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
            <Box sx={{ width: 170 }} ref={(el) => setErrorRef("start_" + schedule.id, el)}>
              <HtmlTooltipDisabled
                title={
                  editLocked
                    ? EXPIRED_LOCK_TOOLTIP
                    : startLocked
                      ? ACTIVE_HOURS_LOCK_TOOLTIP
                      : ""
                }
              >
                <span>
                  <DateInputField
                    elemId={"validity_start_" + schedule.id}
                    label="Start*"
                    fieldName="validity_start"
                    reserveErrorSpace
                    defaultVal={schedule.validity_start || ""}
                    disabled={startLocked}
                    preserveStoredValue={startLocked}
                    minSelectable={startLocked ? undefined : startMinDate}
                    maxSelectable={schedule.validity_end || "2099-12-31"}
                    onChangeCall={(date) =>
                      onUpdate({ validity_start: date ? moment(date).format("YYYY-MM-DD") : null })
                    }
                    onWeekSelect={(monday) => {
                      onUpdate({ validity_start: moment(monday).format("YYYY-MM-DD") });
                    }}
                    weekSelectionMinDate={
                      startLocked
                        ? new Date(2000, 0, 1)
                        : moment(startMinDate, "YYYY-MM-DD").toDate()
                    }
                    weekSelectionMaxDate={
                      schedule.validity_end
                        ? moment(schedule.validity_end).toDate()
                        : new Date(2099, 11, 31)
                    }
                    fieldError={showFieldErr("start_" + schedule.id)}
                    highlightedDates={highlightForDate}
                    highlightLegend={scheduleLegend}
                  />
                </span>
              </HtmlTooltipDisabled>
            </Box>

            <Box sx={{ width: 170 }} ref={(el) => setErrorRef("end_" + schedule.id, el)}>
              <HtmlTooltipDisabled
                title={
                  editLocked
                    ? EXPIRED_LOCK_TOOLTIP
                    : schedule.no_end
                      ? "This schedule has no end date"
                      : ""
                }
              >
                <span>
                  <DateInputField
                    elemId={"validity_end_" + schedule.id}
                    label="End"
                    fieldName="validity_end"
                    reserveErrorSpace
                    defaultVal={schedule.validity_end || ""}
                    disabled={schedule.no_end || editLocked}
                    preserveStoredValue={editLocked}
                    minSelectable={
                      editLocked
                        ? undefined
                        : startLocked
                          ? today
                          : (schedule.validity_start || startMinDate)
                    }
                    maxSelectable={"2099-12-31"}
                    onChangeCall={(date) =>
                      onUpdate({ validity_end: date ? moment(date).format("YYYY-MM-DD") : null })
                    }
                    onWeekSelect={(_monday, sunday) => {
                      onUpdate({ validity_end: moment(sunday).format("YYYY-MM-DD") });
                    }}
                    weekSelectionMinDate={
                      editLocked
                        ? new Date(2000, 0, 1)
                        : startLocked
                          ? moment(today, "YYYY-MM-DD").toDate()
                          : moment(schedule.validity_start || startMinDate, "YYYY-MM-DD").toDate()
                    }
                    weekSelectionMaxDate={new Date(2099, 11, 31)}
                    fieldError={schedule.no_end ? "" : showFieldErr("end_" + schedule.id)}
                    highlightedDates={highlightForDate}
                    highlightLegend={scheduleLegend}
                  />
                </span>
              </HtmlTooltipDisabled>
            </Box>

            {/* The checkbox has no label/input of its own, so the top-aligned row
                would put it at the very top. Padding, not margin — the parent
                Stack's spacing resets child margins. 24px reproduces where the
                bottom-aligned row used to place it: the date field's box is 1px
                (picker offset) + 16px (label gap) + 31px (input) + 16px (reserved
                error line) tall, and this 28px control sat 12px above its bottom. */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pt: "24px" }}>
              {noEndBlocked ? (
                <HtmlTooltipDisabled title={noEndBlockedReason}>
                  <span>
                    <StyledCheckbox checked={schedule.no_end === true} disabled />
                  </span>
                </HtmlTooltipDisabled>
              ) : (
                <StyledCheckbox
                  checked={schedule.no_end}
                  onChange={(e) =>
                    onUpdate({
                      no_end: e.target.checked,
                      validity_end: e.target.checked ? null : schedule.validity_end,
                    })
                  }
                />
              )}
              <Typography
                variant="caption"
                sx={{ position: "relative", top: "1.8px", ...(editLocked && { color: "text.disabled" }) }}
              >
                No End
              </Typography>
            </Stack>
          </Stack>

          {/* mb matches Validity's header→field gap; the date fields' reserved
              error line already separates this section from the inputs above. */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              Opening Schedule
            </Typography>
            <HtmlTooltip
              title={
                <Box width={280}>
                  <Typography variant="body2">
                    Closing time must be after the opening time.
                  </Typography>
                </Box>
              }
            >
              <Info sx={{ fontSize: 16, color: "#62626E", cursor: "pointer" }} />
            </HtmlTooltip>
          </Stack>

          {/* Same Times All Days — the row keeps flex-start so extra timeslots
              grow downward. The toggle+label column carries its own minHeight so
              the OFF state reserves the same space as the ON state, and centers
              its content so the toggle stays vertically centered in that band.
              Bottom pad matches WeekdayRow (DAY_ROW_PAD only): TimeRangeRow already
              contributes pb: 1.5, so an extra pb/mb here stacked too much headroom
              under the Same Times pickers vs the per-day rows. */}
          <Stack
            direction="row"
            alignItems="flex-start"
            sx={{ pb: `${DAY_ROW_PAD}px`, borderBottom: "1px solid #e8e8e8" }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minHeight: 48, minWidth: 210, flexShrink: 0, mt: -1.2 }}>
              <HtmlTooltipDisabled title={hoursLocked ? hoursLockTooltip : ""}>
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <StyledSwitch
                    checked={schedule.same_times_all_days}
                    disabled={hoursLocked}
                    size="small"
                    onChange={(e) => onUpdate({ same_times_all_days: e.target.checked })}
                  />
                </Box>
              </HtmlTooltipDisabled>
              <Typography variant="body2" sx={{ ...(hoursLocked && { color: "text.disabled" }) }}>
                Same Times All Days
              </Typography>
            </Stack>

            <Box sx={{ flex: 1 }} />

            {/* Time controls for Same Times — only visible when Same Times All Days is ON */}
            {schedule.same_times_all_days && (
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ pt: 0.5 }}>
                  <HtmlTooltipDisabled title={hoursLocked ? hoursLockTooltip : ""}>
                    <span>
                      <ToggleButton
                        value="24h"
                        selected={schedule.same_times_is_24h === true}
                        disabled={hoursLocked}
                        onChange={() => onUpdate({ same_times_is_24h: !schedule.same_times_is_24h })}
                        size="small"
                        sx={{
                          height: 32,
                          px: 1.5,
                          border: "1px solid #ccc",
                          "&.Mui-selected": {
                            bgcolor: "#AF3241",
                            color: "#fff",
                            "&:hover": { bgcolor: "#8a2833" },
                          },
                        }}
                      >
                        <Typography variant="caption">24h</Typography>
                      </ToggleButton>
                    </span>
                  </HtmlTooltipDisabled>
                </Box>

                {/* Reserve just enough for a single time range so switching
                    between 24h, one timeslot and Closed never shifts the rows
                    below — same idea as WeekdayRow's minHeight. Extra timeslots
                    or a field error still grow past it, which is fine. */}
                <Box sx={{ minWidth: 250, minHeight: 48 }}>
                  {schedule.same_times_is_24h ? (
                    <Box sx={{ height: 32, mt: 0.5, display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{ textAlign: "left", color: hoursLocked ? "text.disabled" : "#62626E" }}
                      >
                        Open 24 Hours
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={0.75}>
                      {sameTimesRanges.map((range, idx) => (
                        <Box
                          key={idx}
                          ref={(el) => setErrorRef(`same_time_gap_${schedule.id}_${idx}`, el)}
                        >
                          <TimeRangeRow
                            range={range}
                            idx={idx}
                            ranges={sameTimesRanges}
                            showLabels={idx === 0}
                            isFirst={idx === 0}
                            disabled={hoursLocked}
                            lockTooltip={hoursLockTooltip}
                            emptyError={fieldErr(`same_time_gap_${schedule.id}_${idx}`)}
                            onChangeOpen={(val) => handleSameTimesChange(idx, "open", val)}
                            onChangeClose={(val) => handleSameTimesChange(idx, "close", val)}
                            onAdd={handleAddSameTimesRange}
                            onRemove={() => handleRemoveSameTimesRange(idx)}
                          />
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            )}
          </Stack>

          {WEEKDAY_DISPLAY_ORDER.map((day, idx) => (
            <WeekdayRow
              key={day}
              day={day}
              dayData={schedule.weekdays[day]}
              sameTimesActive={schedule.same_times_all_days}
              disabled={hoursLocked}
              lockTooltip={hoursLockTooltip}
              onUpdate={(patch) => updateWeekday(day, patch)}
              isLast={idx === WEEKDAY_DISPLAY_ORDER.length - 1}
              scheduleId={schedule.id}
              fieldErrors={fieldErrors}
              setErrorRef={setErrorRef}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

const StoreOpeningHours = ({ fieldErrors, usedFor }) => {
  const userStateDetails = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  // One wrapper element per schedule card, keyed by schedule id, so a freshly
  // added schedule can be scrolled into view.
  const scheduleRefs = React.useRef({});
  // Scroll targets for the Schedule Overview messages, keyed by the fieldErrors
  // key that produces them. Schedule-level problems (no end date, coverage,
  // overlap, gap) are only ever written here, so the scroll has to land on the
  // message itself — the cards carry no text for them.
  const overviewRefs = React.useRef({});
  // Scroll targets inside the schedule cards, keyed by the same fieldErrors keys:
  // the two validity date fields, the name field, each time range and the
  // "at least one day" line.
  const errorRefs = React.useRef({});

  const setOverviewRef = (key, el) => {
    if (el) overviewRefs.current[key] = el;
    else delete overviewRefs.current[key];
  };

  const setErrorRef = React.useCallback((key, el) => {
    if (el) errorRefs.current[key] = el;
    else delete errorRefs.current[key];
  }, []);

  const [showExpired, setShowExpired] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  // Schedules exempt from the Show Expired filter: the card that holds focus,
  // plus any still inside the grace window after losing it. A schedule in the
  // past cannot be created in the first place — an End date typed below today
  // only reads as expired for the moment between the keystroke and the field's
  // blur validation clearing it, and the card must not vanish in that gap.
  const [exemptScheduleIds, setExemptScheduleIds] = React.useState([]);

  const today = new Date().toISOString().slice(0, 10);

  const schedules =
    typeof userStateDetails.new_store_loc_data !== "undefined" &&
      Array.isArray(userStateDetails.new_store_loc_data.opening_schedules)
      ? userStateDetails.new_store_loc_data.opening_schedules
      : [];

  // First-seen validity per id — locks read this, not live edits.
  const loadedValidityRef = React.useRef({});
  schedules.forEach((s) => {
    if (!loadedValidityRef.current[s.id]) {
      loadedValidityRef.current[s.id] = {
        validity_start: s.validity_start || null,
        validity_end: s.validity_end || null,
        no_end: s.no_end === true,
      };
    }
  });

  const updateSchedules = (updated) => {
    const existing =
      typeof userStateDetails.new_store_loc_data !== "undefined"
        ? userStateDetails.new_store_loc_data
        : {};
    dispatchAction(updateStateData({ new_store_loc_data: { ...existing, opening_schedules: updated } }));
  };

  const handleAddSchedule = () => {
    const newSchedule = DEFAULT_SCHEDULE();
    updateSchedules([...schedules, newSchedule]);
    // Scroll to the TOP of the new schedule (its name field) rather than the
    // bottom of the list, so the user starts entering data from the first field.
    setTimeout(() => {
      scheduleRefs.current[newSchedule.id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleCopySchedule = (source) => {
    const newSchedule = {
      ...DEFAULT_SCHEDULE(),
      same_times_all_days: source.same_times_all_days,
      same_times_is_24h: source.same_times_is_24h,
      same_times_ranges: JSON.parse(JSON.stringify(source.same_times_ranges || [{ open: "08:00", close: "20:00" }])),
      weekdays: JSON.parse(JSON.stringify(source.weekdays)),
    };
    updateSchedules([...schedules, newSchedule]);
    setTimeout(() => {
      scheduleRefs.current[newSchedule.id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleDeleteSchedule = (id) => {
    updateSchedules(schedules.filter((s) => s.id !== id));
  };

  // Deleting a schedule asks for confirmation, except when the card is a brand-new
  // one still holding its default values — that only happens by an accidental "Add
  // Schedule" click, so removing it needs no prompt. Any edited card, whether
  // already saved (real id) or newly added and touched, requires confirmation.
  const requestDeleteSchedule = (schedule) => {
    if (isPristineSchedule(schedule)) {
      handleDeleteSchedule(schedule.id);
    } else {
      setDeleteTarget(schedule);
    }
  };

  const handleUpdateSchedule = (id, patch) => {
    updateSchedules(schedules.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const isExpired = (s) => isExpiredValidity(s, today);

  // Pending grace timers, keyed by schedule id.
  const graceTimersRef = React.useRef({});

  const exemptSchedule = (id) => {
    clearTimeout(graceTimersRef.current[id]);
    delete graceTimersRef.current[id];
    setExemptScheduleIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  };

  // Focus is gone, but the field's blur validation still has to run and clear an
  // End date below today — and the picker retries its own blur up to 300ms later,
  // while the schedule name commits on a 2s debounce. Hold the exemption for the
  // grace window; whatever is still expired once it lapses is expired for real.
  const releaseScheduleAfterGrace = (id) => {
    clearTimeout(graceTimersRef.current[id]);
    graceTimersRef.current[id] = setTimeout(() => {
      delete graceTimersRef.current[id];
      setExemptScheduleIds((ids) => ids.filter((x) => x !== id));
    }, EXPIRY_GRACE_MS);
  };

  React.useEffect(
    () => () => {
      Object.values(graceTimersRef.current).forEach(clearTimeout);
    },
    []
  );

  // focusout also fires when focus moves between two fields of the same card, and
  // the date picker's calendar is portalled outside it — so settle on the next
  // tick and check where focus actually landed before starting the grace window.
  const handleCardFocusOut = (scheduleId, event) => {
    const cardEl = event.currentTarget;
    setTimeout(() => {
      const active = document.activeElement;
      if (cardEl.contains(active)) return;
      if (active && active.closest(".MuiPickersPopper-root")) return;
      releaseScheduleAfterGrace(scheduleId);
    }, 0);
  };

  // Schedule cards keep their stored order while editing. Sorting by start date
  // only happens when existing schedules are loaded (see edit/configure load),
  // because live re-sorting on each date change is confusing to use.
  const displayedSchedules =
    usedFor !== "update" || showExpired
      ? schedules
      : schedules.filter((s) => !isExpired(s) || exemptScheduleIds.includes(s.id));

  const sortedOverview = [...schedules]
    .filter((s) => s.active !== false && !isExpired(s))
    .sort((a, b) => {
      if (!a.validity_start) return 1;
      if (!b.validity_start) return -1;
      return a.validity_start > b.validity_start ? 1 : -1;
    });

  const overviewErrors = computeScheduleErrors(schedules);

  const fieldErr = (key) =>
    typeof fieldErrors !== "undefined" &&
      typeof fieldErrors[key] !== "undefined" &&
      fieldErrors[key] !== ""
      ? fieldErrors[key]
      : "";

  // The one error a failed Next/Save scrolls to, picked by a fixed hierarchy so
  // the same set of errors always resolves to the same place — no walking, no
  // guessing, no different target on the second click:
  //   1. anything in the Schedule Overview box at the top of the page
  //   2. otherwise the first schedule whose validity date pickers are wrong
  //   3. otherwise the first remaining in-card error (name, time ranges, days)
  // Within a tier the topmost error wins, so as errors get fixed the target
  // simply moves down the page. Keys with no rendered element are skipped — an
  // overview ref only exists while its message shows, and a card ref only while
  // that field is on screen.
  const resolveErrorTarget = () => {
    const overview = [];
    const dates = [];
    const rest = [];

    const push = (bucket, refs, key) => {
      if (fieldErr(key) && refs.current[key]) bucket.push(refs.current[key]);
    };
    const pushOverview = (key) => push(overview, overviewRefs, key);
    const pushDate = (key) => push(dates, errorRefs, key);
    const pushRest = (key) => push(rest, errorRefs, key);

    pushOverview("no_end");
    pushOverview("schedules");
    sortedOverview.forEach((s) => {
      ["no_coverage_", "overlap_", "gap_after_", "gap_before_", "days_"].forEach((p) =>
        pushOverview(p + s.id)
      );
    });

    displayedSchedules.forEach((s) => {
      pushDate("start_" + s.id);
      pushDate("end_" + s.id);
    });

    // Card order: name, then the Same Times ranges, then the weekday rows top to
    // bottom, then the "at least one day" line under them.
    displayedSchedules.forEach((s) => {
      pushRest("name_" + s.id);
      (s.same_times_ranges || []).forEach((_, idx) =>
        pushRest(`same_time_gap_${s.id}_${idx}`)
      );
      WEEKDAY_DISPLAY_ORDER.forEach((day) => {
        (s.weekdays?.[day]?.time_ranges || []).forEach((_, idx) =>
          pushRest(`time_gap_${s.id}_${day}_${idx}`)
        );
      });
    });

    if (overview.length > 0) return { el: overview[0], isOverview: true };
    if (dates.length > 0) return { el: dates[0], isOverview: false };
    if (rest.length > 0) return { el: rest[0], isOverview: false };
    return null;
  };

  // fieldErrors is set fresh on each failed attempt, so this fires once per click.
  React.useEffect(() => {
    const target = resolveErrorTarget();
    if (!target) return;

    // One scroll, never two. Scrolling to the card first and then correcting onto
    // the error inside it read as a stutter, because both animations run smooth
    // and overlap. An overview message can be reached straight away; a target
    // inside a card has to wait out the Collapse the validator opens for an
    // errored card first (MUI timeout="auto", ~400ms at full card height),
    // otherwise its position is still moving when the scroll starts.
    if (target.isOverview) {
      target.el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const timer = setTimeout(() => {
      target.el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, COLLAPSE_SETTLE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldErrors]);

  return (
    <>
      <Box height={10} />

      <Typography variant="body2" sx={{ color: "#62626E", mb: 0.5 }}>
        Define the regular opening hour schedules for the store.
      </Typography>
      <Typography variant="body2" sx={{ color: "#62626E", mb: 0.5 }}>
        Schedules must not overlap or contain gaps. The first schedule must start on the store's creation date, and at least one schedule must have indefinite validity.
      </Typography>
      <Typography variant="body2" sx={{ color: "#62626E", mb: 1.5 }}>
        Special opening schedules and quiet hours can be configured in the store's settings.
      </Typography>

      <StyledGreyInfoBox sx={{ mb: 2, p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
          Schedule Overview
        </Typography>

        {overviewErrors._noEnd && (
          <Typography
            variant="body2"
            sx={{ color: "#AF3241", mb: 1 }}
            ref={(el) => setOverviewRef("no_end", el)}
          >
            {overviewErrors._noEnd}
          </Typography>
        )}

        {/* "At least one schedule required" only applies while the list is empty;
            drop it as soon as a schedule is added instead of lingering until save. */}
        {schedules.length === 0 && fieldErr("schedules") && (
          <Typography
            variant="body2"
            sx={{ color: "#AF3241", mb: 1 }}
            ref={(el) => setOverviewRef("schedules", el)}
          >
            {fieldErr("schedules")}
          </Typography>
        )}

        {sortedOverview.length === 0 ? (
          <Typography variant="body2" sx={{ color: "#62626E" }}>
            No schedules added yet.
          </Typography>
        ) : (
          sortedOverview.map((s) => {
            const start = s.validity_start ? formatDateDMY(s.validity_start) : "—";
            const end = s.no_end ? "Indefinitely" : s.validity_end ? formatDateDMY(s.validity_end) : "—";
            // Each message is tagged with the fieldErrors key it mirrors so the
            // save-time scroll can target the rendered line. A schedule with a gap
            // on both sides still shows one line — the unused key simply registers
            // no ref and is skipped.
            const gapKey = overviewErrors["gap_after_" + s.id] ? "gap_after_" : "gap_before_";
            const scheduleErrs = [
              { key: "no_coverage_" + s.id, msg: overviewErrors["no_coverage_" + s.id] },
              { key: "overlap_" + s.id, msg: overviewErrors["overlap_" + s.id] },
              { key: gapKey + s.id, msg: overviewErrors[gapKey + s.id] },
              { key: "days_" + s.id, msg: overviewErrors["days_" + s.id] },
            ].filter((e) => e.msg);
            return (
              <Box key={s.id} sx={{ mb: 0.5 }}>
                <Stack direction="row" spacing={4} alignItems="flex-start">
                  <Typography variant="body2" sx={{ width: 460, flexShrink: 0, wordBreak: "break-word" }}>
                    <strong>{s.name || "Unnamed"}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ width: 240, flexShrink: 0 }}>
                    {start} – {end}
                  </Typography>
                  {scheduleErrs.length > 0 && (
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                      {scheduleErrs.map((e) => (
                        <Typography
                          key={e.key}
                          variant="body2"
                          ref={(el) => setOverviewRef(e.key, el)}
                          sx={{
                            color: "#AF3241",
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            lineHeight: 1.3,
                          }}
                        >
                          {e.msg}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Box>
            );
          })
        )}
      </StyledGreyInfoBox>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 2 }}>
        <ActionButton label="Add Schedule" icon={<Add />} onClick={handleAddSchedule} sx={{ mb: 0 }} />
        {/* Show Expired is not relevant during store creation — no past schedules exist */}
        {usedFor === "update" && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <StyledSwitch
              checked={showExpired}
              size="small"
              onChange={(e) => setShowExpired(e.target.checked)}
            />
            <Typography variant="body2">Show Expired</Typography>
          </Stack>
        )}
      </Stack>

      {displayedSchedules.map((schedule) => (
        <Box
          key={schedule.id}
          ref={(el) => {
            if (el) scheduleRefs.current[schedule.id] = el;
            else delete scheduleRefs.current[schedule.id];
          }}
          // focusin/focusout bubble, so these cover every control in the card.
          onFocus={() => exemptSchedule(schedule.id)}
          onBlur={(e) => handleCardFocusOut(schedule.id, e)}
        >
          <ScheduleCard
            schedule={schedule}
            schedules={schedules}
            usedFor={usedFor}
            loadedValidity={loadedValidityRef.current[schedule.id]}
            onUpdate={(patch) => handleUpdateSchedule(schedule.id, patch)}
            onDelete={() => requestDeleteSchedule(schedule)}
            onCopy={() => handleCopySchedule(schedule)}
            fieldErrors={fieldErrors}
            setErrorRef={setErrorRef}
          />
        </Box>
      ))}

      <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
        <Tooltip title="Add schedule">
          <IconButton onClick={handleAddSchedule} sx={{ border: "1px solid #e0e0e0" }}>
            <Add />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box height={20} />

      {deleteTarget && (
        <Confirmation
          showConfirmationPopup={!!deleteTarget}
          closePopup={() => setDeleteTarget(null)}
          confirmationActionCall={() => handleDeleteSchedule(deleteTarget.id)}
          confirmationMessage="Are you certain you want to delete this schedule?"
          confirmationTitle="Delete Schedule"
        />
      )}
    </>
  );
};

export default StoreOpeningHours;
